import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../../sdk/src/components/ibase-page/ibase-page.component';
import {firstValueFrom, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {UserCaching} from '../../../caching/user.caching';
import {EDialogOpenMode} from '../../../../../sdk/src/enums/dialog-open-mode.enum';
import {CpmCUserApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {CpmCServiceRequestApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request.api-service';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {CpmCOrganizationApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-organization.api-model';
import {NewEditOrganizationModalComponent} from '../../../modals/new-edit-organization-modal/new-edit-organization-modal.component';
import {CpmCOrganizationApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-organization.api-service';

/**
 * Organization General Information Component
 *
 * Displays general information for an organization including:
 * - Read-only metadata (ID, organization, created date/user)
 * - Statistics overview (service requests, files, object store counts)
 *
 * Provides navigation to related tabs and modal dialogs for editing the organization.
 */
@Component({
    selector: 'app-organization-general-info',
    templateUrl: './organization-general-info.component.html',
    styles: `
        :host {
            display: block;
            height: 100%;
        }

        .full-height-container {
            height: 100%;
        }

        .flex-column-grow {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            min-height: 0;
        }

        .info-col {
            display: flex;
            flex-direction: column;
            align-content: space-between;
            flex-wrap: wrap;
            justify-content: space-around;

            & > div {
                height: 2.5rem;
            }
        }

        ::ng-deep .organization-panel {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            min-height: 0;
            height: 100%;
        }

        ::ng-deep .organization-panel .p-panel-content {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            min-height: 0;
        }
    `,
    imports: [CommonModule, PrimeNgImportsModule]
})
export class OrganizationGeneralInfoComponent extends IBasePageComponent implements OnChanges {
    protected createdByUser?: CpmCUserApiModel;
    protected isRefreshing = false;

    protected serviceRequestsCount?: number;
    protected filesCount?: number;
    protected objectStoreCount?: number;

    // ===============================================================
    // ===============================================================

    /**
     * The organization to display and edit
     */
    @Input() organization?: CpmCOrganizationApiModel;
    /**
     * Observable that triggers statistics refresh when data changes in other tabs
     */
    @Input() refreshTrigger$?: Observable<void>;
    /**
     * Event emitted when organization is updated
     */
    @Output() organizationUpdated = new EventEmitter<void>();

    // ===============================================================
    // ===============================================================

    /**
     * Initializes the component
     */
    constructor() {
        super();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        // Subscribe to refresh trigger to reload statistics when data changes in other tabs
        this.refreshTrigger$?.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.loadCounts();
        });

        await this.asyncCritical(async () => {
            if (this.organization?.created_by_uuid) {
                UserCaching.getUserBatched(this.organization.created_by_uuid).subscribe((user) => {
                    this.createdByUser = user;
                });
            }
        });
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        // No cleanup required
    }

    // ===============================================================
    // ===============================================================

    /**
     * Angular lifecycle hook called when input properties change.
     * Loads related data when organization changes.
     *
     * @param changes - Object containing the changed properties
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['organization'] && this.organization) {
            this.loadCounts();
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Loads count statistics for service requests, files, and object store entries.
     * Updates the component properties with the retrieved counts.
     */
    private loadCounts(): void {
        if (!this.organization?.uuid) return;

        // Service Requests count
        firstValueFrom(
            CpmCServiceRequestApiService.getServiceRequestList({organization_uuid: this.organization.uuid}, {limit: 1})
        ).then((result) => (this.serviceRequestsCount = result.total));

        // Object Store count
        firstValueFrom(
            CpmCObjectStoreEntryProxyApiService.getObjectStoreEntryProxyList(
                'organization',
                this.organization.uuid,
                {},
                {limit: 1}
            )
        ).then((result) => (this.objectStoreCount = result.total));
    }

    // ===============================================================
    // ===============================================================

    /**
     * Opens the organization modal for editing
     */
    protected openOrganizationModal(): void {
        if (!this.organization) return;

        const dialogRef = this.globalsService.dialogService.open(
            NewEditOrganizationModalComponent,
            NewEditOrganizationModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.EDIT,
                extended: {
                    existing: this.organization
                }
            })
        );

        // Refresh data when modal closes
        dialogRef.onClose.subscribe(async (result) => {
            if (result && this.organization && result.item) {
                this.isRefreshing = true;

                try {
                    // Fetch fresh organization data from the server
                    const updatedOrganization = await firstValueFrom(
                        CpmCOrganizationApiService.getOrganizationByUUID(this.organization.uuid!)
                    );

                    // Update the organization object with fresh data
                    Object.assign(this.organization, updatedOrganization);

                    // Reload user info if changed
                    if (updatedOrganization.created_by_uuid) {
                        UserCaching.getUserBatched(updatedOrganization.created_by_uuid).subscribe((user) => {
                            this.createdByUser = user;
                        });
                    }

                    // Emit event to notify parent that organization was updated
                    this.organizationUpdated.emit();
                } catch (error) {
                    console.error('Failed to refresh organization data:', error);
                } finally {
                    this.isRefreshing = false;
                }
            }
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Navigates to the specified tab in the parent page.
     * Updates URL query parameters to switch tabs.
     *
     * @param tab - The tab identifier to navigate to
     */
    protected navigateToTab(tab: string): void {
        this.globalsService.router.navigate([], {
            relativeTo: this.globalsService.activatedRoute,
            queryParams: {tab},
            queryParamsHandling: 'merge'
        });
    }
}
